import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { humanizeApiError } from '@lapo/core';
import type { ApiClient, RedditResults } from './auth/apiClient.js';

/** Where the setup call-to-action points (config lives in the integration card, not here). */
const SETUP_MESSAGE: Record<string, string> = {
  'not-configured': 'Set up the Reddit Monitor in Settings → Integrations: add your redditapis key and a subreddit to watch.',
  'no-channels': 'Add a subreddit to watch in Settings → Integrations → Reddit Monitor.',
  'no-key': 'Add your redditapis API key in Settings → Integrations → Reddit Monitor.',
};

/**
 * Reddit Monitor side-panel — RESULTS ONLY. Configuration (BYO key + channels + filters) lives in the
 * integration card (Settings → Integrations → Reddit Monitor), like every other integration. This view
 * fetches the filtered matches on demand (each refresh spends redditapis credits) and lists them.
 */
export function RedditMonitorView({ api }: { api: ApiClient | null }): ReactElement {
  const [results, setResults] = useState<RedditResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const loadResults = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    setError(undefined);
    try {
      setResults(await api.getRedditResults());
    } catch (err) {
      setError(`Couldn't fetch results: ${humanizeApiError(err)}`);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void loadResults();
  }, [loadResults]);

  const posts = results?.posts ?? [];
  const setupHint = results?.error ? SETUP_MESSAGE[results.error] : undefined;

  return (
    <div className="lapo-reddit">
      <div className="lapo-reddit-head">
        <h1 className="lapo-reddit-title">Reddit</h1>
        <button className="lapo-reddit-btn is-primary" onClick={() => void loadResults()} disabled={loading || !api}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && <p className="lapo-reddit-error">{error}</p>}
      {setupHint && <p className="lapo-reddit-hint">{setupHint}</p>}

      <div className="lapo-reddit-list">
        {!setupHint && posts.length === 0 && !loading && (
          <p className="lapo-reddit-empty">No matching posts right now.</p>
        )}
        {posts.map((p) => (
          <div className="lapo-reddit-card" key={`${p.channel}:${p.id}`}>
            {p.image && (
              <a href={p.link || p.url} target="_blank" rel="noreferrer" className="lapo-reddit-thumb">
                <img src={p.image} alt="" loading="lazy" />
              </a>
            )}
            <div className="lapo-reddit-card-body">
              <a href={p.url} target="_blank" rel="noreferrer" className="lapo-reddit-card-title">
                {p.title}
              </a>
              <div className="lapo-reddit-meta">
                <span className="lapo-reddit-badge">r/{p.channel}</span>
                <span>{p.author}</span>
                {p.seller && <span className="lapo-reddit-seller">{p.seller}</span>}
                <span>{p.numComments} comments</span>
                {p.created && <span>{p.created.slice(0, 10)}</span>}
                {p.link && p.link !== p.url && (
                  <a href={p.link} target="_blank" rel="noreferrer">
                    photos
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
